import LocationCommonWellItem from './LocationCommonWellItem';
import getLocationPickerProps from '../utils/getLocationPickerProps';
import LocationCommonWellItemViewState from '../store/schema/LocationCommonWellItemViewState';
import LocationPickerState, { SearchContext } from '../store/schema/LocationPickerState';
import { observer } from 'mobx-react';
import { IInputProps } from '@fluentui/react/lib/Pickers';
import requestCurrentPosition from '../utils/requestCurrentPosition';
import * as React from 'react';
import {
    FloatingPeoplePicker,
    IPeopleFloatingPickerProps,
} from '@fluentui/react/lib/FloatingPicker';
import {
    ReadWriteCommonWell,
    ReadWriteCommonWellProps,
    ReadWriteCommonWellState,
} from 'owa-readwrite-common-well/lib/components/ReadWriteCommonWell';
import {
    ISelectedPeopleProps,
    ISelectedPeopleItemProps,
} from '@fluentui/react/lib/SelectedItemsList';
import { createLocationPersonaFromStringLocation } from 'owa-location-persona';
import getDefaultPickerState from '../utils/getDefaultPickerState';
import { LocationPickerBaseProps } from './FindLocationControl';
import { isNullOrWhiteSpace } from 'owa-string-utils';
import { logUsage } from 'owa-analytics';
import { getExtendedTheme } from 'owa-fabric-theme';

export interface LocationCommonWellProps
    extends LocationPickerBaseProps,
        ReadWriteCommonWellProps<
            LocationCommonWellItemViewState,
            LocationCommonWellItemViewState,
            LocationCommonWellItemViewState
        > {
    inputProps: IInputProps;
    onLocationsChanged?: (items: LocationCommonWellItemViewState[]) => void;
    isSmallSize?: boolean; // size of the well selected items
    isItemAligned?: boolean; // pill aligned with placeholder text
    maxItemWidth?: number; // max size of selected items
    itemLimit?: number; // max count of selected items
    itemClassName?: string;
    onRenderFBBadge?: (emailAddress: string) => JSX.Element;
    onQueryStringChanged?: (queryString: string) => void;
    shouldClearInput?: boolean;
    readOnlyMode?: boolean;
    disabled?: boolean;
}

export interface LocationCommonWellState
    extends ReadWriteCommonWellState<LocationCommonWellItemViewState>,
        LocationPickerState {}

const styles = require('./LocationCommonWell.scss');

@observer
export default class LocationCommonWell extends ReadWriteCommonWell<
    LocationCommonWellProps,
    LocationCommonWellItemViewState, // suggestion type
    LocationCommonWellItemViewState, // resolved item type
    LocationCommonWellItemViewState, // external (controlled component) selectedItems
    LocationCommonWellState
> {
    constructor(props: LocationCommonWellProps) {
        super(props);
        this.state = {
            items: [], // resolved item set
            // Picker state
            ...getDefaultPickerState(),
        };
    }

    protected getFindControlProps = (): Partial<IPeopleFloatingPickerProps> => {
        return getLocationPickerProps(
            this.getPickerState,
            this.onPickerStateChanged,
            this.getSearchContext,
            this.props.theme ?? getExtendedTheme(),
            this.props.colorScheme,
            this.props.roomFreeBusyEnabled,
            this.props.disableUseThisLocationButton,
            this.onUseThisLocation,
            this.props.zeroQueryDisabled,
            this.props.showAddRoom,
            this.props.onAddRoomClicked,
            this.props.calloutWidth,
            this.getFindControl(),
            this.props.canRemoveSuggestion,
            this.props.onQueryStringChanged
        );
    };

    // Extra props for the selected item collection. Not used for the location well
    protected getExtendedSelectedItemsListProps = (): ISelectedPeopleProps => {
        return null;
    };

    // Call back when an item is selected from the drop down. Return null to filter duplicates
    protected onItemSelected = (
        item: LocationCommonWellItemViewState
    ): LocationCommonWellItemViewState => {
        return !this.containLocation(item) ? item : null;
    };

    // Extra props for the well control
    protected extendedPickerProps(): any {
        return {
            inputProps: {
                ...this.props.inputProps,
                onFocus: this.onInputFocus,
            },
            itemLimit: this.props.itemLimit,
            className: styles.input,
            disabled: this.props.readOnlyMode || this.props.disabled,
        };
    }

    protected renderSelectedItem = (props: ISelectedPeopleItemProps): JSX.Element => {
        const { theme = getExtendedTheme(), isSmallSize, isItemAligned, maxItemWidth } = this.props;
        const viewState = props.item as LocationCommonWellItemViewState;
        viewState.blockWellItemRemoval = this.props.readOnlyMode || this.props.disabled;

        // clear picker state if unresolved string location is already force resolved
        if (this.props.shouldClearInput) {
            this.picker.clearInput();
        }

        return (
            <LocationCommonWellItem
                key={`${viewState?.persona?.personaId}_${props.index}`}
                index={props.index}
                viewState={viewState}
                removeOperation={this.removeLocation}
                useContextMenu={true}
                isSelected={props.selected}
                theme={theme}
                isSmallSize={isSmallSize}
                maxItemWidth={maxItemWidth}
                onRenderFBBadge={this.props.onRenderFBBadge}
                colorScheme={this.props.colorScheme}
                isItemAligned={isItemAligned}
                itemClassName={this.props.itemClassName}
            />
        );
    };

    protected convertSuggestionItemToWellItem = (
        suggestionItem: LocationCommonWellItemViewState
    ): LocationCommonWellItemViewState => {
        return suggestionItem;
    };

    protected convertInitialItemsToWellItems = (
        items: LocationCommonWellItemViewState[]
    ): LocationCommonWellItemViewState[] => {
        return items;
    };

    protected onSelectedItemsChanged = (items: LocationCommonWellItemViewState[]): void => {
        // do nothing
    };

    /* we are overriding the definition of onItemsChanged to map to onLocationsChanged as the reuquierd prop for location*/
    protected onItemsChanged = () => {
        if (this.props.onLocationsChanged) {
            this.props.onLocationsChanged(this.state.items);
        }
    };

    protected onKeyDown = async (ev: React.KeyboardEvent<unknown>) => {
        return null;
    };

    protected getItemIndex = (item: LocationCommonWellItemViewState) => {
        //This is implementing the abstract function in super class.
        //This is used for drag and drop. Since drag and drop does
        //not happen in location well, we return -1 as a generic value
        return -1;
    };

    /** checks if the specified location is already part of the resolved locations */
    private containLocation = (location: LocationCommonWellItemViewState): boolean => {
        // A room in location well items may have its email as the ID, whereas the same room
        // added from Las can have a GUID as Id. We currently use TBA to update the room with its
        // GUID Id, which happens with delays. As a workaround we should compare the locationUri
        // to remove duplicate items.
        const locationUri = location.persona.postalAddress
            ? location.persona.postalAddress.LocationUri
            : null;
        const hasValidLocationUri: boolean = !isNullOrWhiteSpace(locationUri);

        return this.state.items.some(
            resolvedLocation =>
                resolvedLocation.persona.personaId == location.persona.personaId ||
                (hasValidLocationUri &&
                    resolvedLocation.persona.postalAddress &&
                    resolvedLocation.persona.postalAddress.LocationUri == locationUri)
        );
    };

    /**
     * Call back to get the location dropdow (state). It is used to generate the props for the location picker common control
     */
    private getPickerState = (): LocationPickerState => {
        return this.props.getPickerState ? this.props.getPickerState() : this.state;
    };

    /**
     * Call back to handle the location picker state change.
     */
    private onPickerStateChanged = (state: Partial<LocationPickerState>): void => {
        if (this.props.onPickerStateChanged) {
            this.props.onPickerStateChanged(state);
        } else {
            this.setState(state as LocationCommonWellState);
        }
    };

    private getSearchContext = (): SearchContext => {
        const event = this.props.event;
        return {
            event: event,
            searchMode: this.props.searchMode ? this.props.searchMode : 'All',
            currentDevicePosition: this.currentDevicePosition,
        };
    };

    private onUseThisLocation = (locationString: string): void => {
        const newSelectedItem: LocationCommonWellItemViewState = {
            persona: createLocationPersonaFromStringLocation(locationString),
            displayText: locationString,
            isValid: true,
            isSelected: false,
            isContextMenuOpen: false,
        };
        this.onItemsAdded([newSelectedItem]);
        this.picker.clearInput();
        this.picker.floatingPicker.current.hidePicker();

        logUsage('onUseThisLocation');
    };

    private onInputFocus = (): void => {
        requestCurrentPosition(position => {
            this.currentDevicePosition = position;
        });
    };

    private currentDevicePosition: GeolocationPosition;

    private removeLocation = (location: LocationCommonWellItemViewState) => {
        this.picker.selectedItemsList.current.removeItems([location]);
    };

    private getFindControl = (): FloatingPeoplePicker => {
        return this.picker ? this.picker.floatingPicker.current : null;
    };
}
